import { useCallback, useEffect, useState } from 'react';
import { Checkbox, Divider, Flex, Stack, useMantineTheme } from '@mantine/core';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { UnstyledButton } from '~/domains/design-system/Button';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';

import type { StaticFieldProps } from '../../types/common';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';

import type { EditModalField } from './types';

function EditModalTitle({ label }: { label?: string }) {
  return label ? (
    <Title ta="center" variant="headline-large">
      {label}
    </Title>
  ) : null;
}

export function EditModalFieldElement({
  field,
  value,
  onValueChange,
  sectionKey,
  allValues,
  allErrors,
}: StaticFieldProps<EditModalField>) {
  const [isOpen, setIsOpen] = useState(false);

  const theme = useMantineTheme();
  const options = getFilteredSelectOptions({
    options: field.options,
    sectionKey,
    allValues,
    allErrors,
  });

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;
      const targetOption = options.find((option) => `${option.value}` === `${target.value}`);
      if (!targetOption) return;

      onValueChange(targetOption.value);
      setIsOpen(false);
    },
    [onValueChange, options]
  );

  useEffect(() => {
    if (!value) return;
    if (options.find((option) => option.value === value)) return;

    onValueChange(null);
  }, [onValueChange, options, value]);

  if (options.length === 0) {
    return null;
  }

  if (field?.locked) return null;

  return (
    <>
      <Divider />
      <Flex justify="center" /* eslint-disable-line local-rules/use-theme-tokens */ pt={14} pb={theme.other.spacing._6}>
        <UnstyledButton
          disabled={field?.disabled}
          onClick={() => setIsOpen(true)}
          data-test-id={dt.action.editSettings}
        >
          <Text variant="label-small" c={theme.colors.blue[9]}>
            Edit settings
          </Text>
        </UnstyledButton>
      </Flex>

      <Drawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onToggle={setIsOpen}
        title={<EditModalTitle label={field?.label} />}
        data-test-id={dt.drawer.container}
      >
        <Stack>
          {field?.description ? (
            <Text c="gray2.3" ta="center" variant="body-large">
              {field.description}
            </Text>
          ) : null}
          {options?.map((option, i) => {
            const isChecked = value === option.value;

            return (
              <Tooltip
                isRendered={!!option.params?.tooltip}
                withinPortal
                position="top-start"
                key={`${option.value}`}
                label={option.params?.tooltip}
              >
                <span>
                  <Checkbox
                    pb={theme.other.spacing._8}
                    w="100%"
                    radius="lg"
                    data-test-id={dt.checkOption}
                    checked={isChecked}
                    wrapperProps={{ 'data-test-id': dt.field(sectionKey, [field.key]) }}
                    disabled={option.isDisabled}
                    sx={{ borderBottom: `1px solid ${theme.colors.gray2[0]}` }}
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    onChange={() => handleOnChange({ target: { value: option.value } })}
                    label={<Text variant="headline-medium">{option.label}</Text>}
                  />
                </span>
              </Tooltip>
            );
          })}
        </Stack>
      </Drawer>
    </>
  );
}
